import React from "react";
import PropTypes from "prop-types";
import UnderlineInput from "../common/UnderlineInput";
import IsoDateInput from "../common/IsoDateInput";
import SelectDropdown from "../common/SelectDropdown";
import EntryPicker from "../common/EntryPicker";

export const JOURNAL_NAME_OPTIONS = [
  { value: "API", label: "API — Invoice journal" },
  { value: "APP", label: "APP — Payment journal" },
];
const CURRENCY_OPTIONS = ["VND", "USD"].map((value) => ({ value, label: value }));

// Header values of one journal, from the server's section.header.
export const headerState = (h = {}) => ({
  vendor: h.vendor_account || "",
  vendorName: h.vendor_name || "",
  counterparty: h.counterparty_type || "vendor",
  employeeAccount: h.employee_account || "",
  employeeAccountType: h.employee_account_type || "Ledger",
  transDate: (h.trans_date || "").slice(0, 10),
  // Blank on the server = follows the request; show what the journal will carry.
  currency: h.currency || h.currency_effective || "VND",
  description: h.description || "",
});

// What the server stores for that header (PUT journal_overrides › header / journal_headers).
export const headerPayload = (state, { vendorFromPo }) => ({
  ...(!vendorFromPo ? { counterparty_type: state.counterparty, vendor_account: state.vendor.trim(),
    employee_account: state.employeeAccount.trim(), employee_account_type: state.employeeAccountType } : {}),
  trans_date: state.transDate,
  currency: state.currency,
  description: state.description.trim(),
});

export const headerChanged = (state, serverHeader, { vendorFromPo }) => {
  const base = headerState(serverHeader);
  const keys = ["transDate", "currency", "description", ...(!vendorFromPo ? ["counterparty", "employeeAccount", "employeeAccountType"] : [])];
  if (keys.some((k) => state[k] !== base[k])) return true;
  return !vendorFromPo && state.vendor.trim().toUpperCase() !== base.vendor;
};

/**
 * Header of ONE journal: Journal name (primary journal only), currency, date,
 * description and who is paid. Currency only relabels the journal — the amounts
 * stay those of the request.
 */
const JournalHeaderFields = ({
  state, onChange, section, primary, options, onJournalNameChange, editable, created, requestId, paymentRequest: pr,
  vendorFromPo,
}) => {
  const set = (key) => (value) => onChange({ ...state, [key]: value });
  const locked = !editable || created;

  return (
    <div className="payment-journal-header-block">
      <div aria-label={`${section.label} header`} className="payment-journal-setup payment-journal-header">
        <p className="payment-journal-section-title">Journal header</p>
        <div className="payment-journal-header-grid">
          <label className="payment-journal-setup-field">
            <span className="payment-journal-setup-label">Journal name</span>
            {primary && options?.choice_open ? (
              <SelectDropdown aria-label="Journal name" value={section.name} options={JOURNAL_NAME_OPTIONS}
                disabled={!editable} onChange={onJournalNameChange} />
            ) : (
              <span className="payment-journal-header-value">{section.name}</span>
            )}
          </label>
          <label className="payment-journal-setup-field">
            <span className="payment-journal-setup-label">Currency</span>
            <SelectDropdown aria-label={`${section.name} currency`} value={state.currency} options={CURRENCY_OPTIONS}
              disabled={locked} onChange={set("currency")} />
          </label>
          <label className="payment-journal-setup-field">
            <span className="payment-journal-setup-label">Transaction date</span>
            <IsoDateInput aria-label={`${section.name} transaction date`} aria-required="true"
              value={state.transDate} disabled={locked} onChange={set("transDate")} />
          </label>
          <label className="payment-journal-setup-field payment-journal-header-description">
            <span className="payment-journal-setup-label">Description</span>
            <UnderlineInput aria-label={`${section.name} description`} value={state.description} maxLength={500}
              placeholder={section.header?.description_effective || "Description"} disabled={locked}
              onChange={(e) => set("description")(e.target.value)} />
          </label>
        </div>
        {state.currency !== section.header?.currency_effective && section.header?.currency_effective && (
          <p role="note" className="tw-m-0 tw-mt-2 tw-text-[11px] tw-text-geist-fg-3">
            Amounts stay as on the request; only the journal currency label changes.
          </p>
        )}
      </div>

      {/* The vendor is picked on the Vendor line (Account column) of the table. Only an
          employee reimbursement needs more here: whose account the money goes to. */}
      {!vendorFromPo && state.counterparty === 'employee' && (
        <div className="payment-journal-setup payment-journal-counterparty payment-journal-counterparty-employee">
          <label className="payment-journal-setup-field"><span className="payment-journal-setup-label">Employee account type</span>
            <SelectDropdown aria-label={`${section.name} employee account type`} value={state.employeeAccountType} disabled={locked}
              options={['Ledger', 'Vendor'].map((value) => ({ value, label: value }))}
              onChange={(value) => onChange({ ...state, employeeAccountType: value, employeeAccount: '' })} />
          </label>
          <label className="payment-journal-setup-field">
            <span className="payment-journal-setup-label">{state.employeeAccountType === 'Vendor' ? 'Employee Vendor account in AX' : 'Employee payable ledger account'}</span>
            <EntryPicker kind={state.employeeAccountType === 'Vendor' ? 'vendor_account' : 'ledger_account'} paymentRequestId={requestId}
              value={state.employeeAccount} disabled={!editable} onChange={set("employeeAccount")}
              aria-label={`${section.name} employee account`} placeholder="Accounting ledger code…" />
          </label>
          <div className="payment-journal-counterparty-name"><span className="payment-journal-setup-label">Employee</span>
            <div><span>{pr.employee_code || pr.payee || '—'}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

JournalHeaderFields.propTypes = {
  state: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  section: PropTypes.object.isRequired,
  primary: PropTypes.bool,
  options: PropTypes.object,
  onJournalNameChange: PropTypes.func,
  editable: PropTypes.bool,
  created: PropTypes.bool,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  paymentRequest: PropTypes.object.isRequired,
  vendorFromPo: PropTypes.bool,
};

export default JournalHeaderFields;
